import React from "react";
import PopUp from "../../../components/common/PopUp/PopUp";
import { normalizeEmailAddresses, parseEntryNames } from "./membersHelpers";

export interface SponsorCardProps {
  keyName: string;
  title: string;
  justifySelf?: string;
  fallbackMarginLeft?: string;
  paddingLeft?: string;
  sponsorsList?: any;
  openMailClient: (addresses?: string[], subject?: string) => void;
  teamName?: string;
}

const getShorthandKeyName = (keyName: string): string => {
  if (keyName === "Sponsors & Labor Sponsors") return "Sponsors";
  if (keyName === "Co-Leads & Labor Co-Leads") return "Co-Leads";
  if (keyName === "TH/WPS Champion") return "Champions";
  return keyName;
};

const getPrimaryNameCell = (
  entry: any,
  title: string,
  namesFlat: string[],
): React.ReactNode => {
  if (!entry) return <div>No {title} in this team</div>;
  if (namesFlat.length > 0) return <div>{namesFlat[0]}</div>;
  return <div>No names available</div>;
};

const renderExtraNameRows = (
  entry: any,
  namesFlat: string[],
  keyName: string,
): React.ReactNode => {
  if (!entry) return null;
  return namesFlat.slice(3).map((name, idx) => (
    <React.Fragment key={`${keyName}-extra-row-${idx}`}>
      <div className="text-right"></div>
      <div className="text-left" style={{ width: "300px" }}>
        <div>{name}</div>
      </div>
    </React.Fragment>
  ));
};

const escapeHtml = (value: string): string =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("'", "&#39;")
    .replaceAll('"', "&quot;");

const SponsorCard: React.FC<SponsorCardProps> = ({
  keyName,
  title,
  justifySelf = "end",
  fallbackMarginLeft,
  paddingLeft,
  sponsorsList,
  openMailClient,
  teamName,
}) => {
  const entry: any = sponsorsList?.[keyName];
  const shorthandKeyName = getShorthandKeyName(keyName);
  const justifySelfClass =
    justifySelf === "end" ? "justify-self-end" : "justify-self-start";
  const paddingLeftClass = paddingLeft ? `pl-[${paddingLeft}]!` : "";
  const marginLeftClass = fallbackMarginLeft
    ? `ml-[${fallbackMarginLeft}]!`
    : "";
  const namesFlat: string[] = entry ? parseEntryNames(entry) : [];
  const emails = entry ? normalizeEmailAddresses(entry?.emailList) : [];
  const emailListHtml = emails?.length
    ? "<ul>" +
      emails.map((email) => `<li>${escapeHtml(email)}</li>`).join("\n") +
      "</ul>"
    : "<div>No emails available</div>";
  const primaryNameCell = getPrimaryNameCell(entry, title, namesFlat);

  return (
    <div
      className={`sponsor-card ${entry ? shorthandKeyName : shorthandKeyName + "-no-response"} p4 rounded-md min-h-16 ${justifySelfClass} ${paddingLeftClass}`}
    >
      <div className={`sponsor-card-grid ${marginLeftClass}`}>
        <div className="text-right">
          <div className="font-semibold">{title}:</div>
        </div>
        <div className="text-left sponsor-card-name-cell">
          {primaryNameCell}
        </div>
        {entry && (
          <>
            <div className="text-right">
              <button
                type="button"
                onClick={() =>
                  openMailClient(
                    entry?.emailList,
                    `Message to ${shorthandKeyName} of team ${teamName ?? ""}`,
                  )
                }
                className="bg-transparent border-none p-0 text-kp-blue-dark underline cursor-pointer"
              >
                <span className="inline-flex items-center gap-1.5">
                  <span className="sponsor-card-link-text">
                    Email {shorthandKeyName}
                  </span>
                  <span>
                    <img
                      src="/assets/images/signifier.svg"
                      alt=""
                      width={12}
                      height={12}
                    />
                  </span>
                </span>
              </button>
            </div>
            <div className="text-left sponsor-card-name-cell">
              {namesFlat.length > 1 ? <div>{namesFlat[1]}</div> : <div></div>}
            </div>
            <div className="text-right">
              <button
                type="button"
                className="bg-transparent border-none p-0 text-kp-blue-dark underline cursor-pointer"
              >
                <span className="inline-flex items-center gap-1.5">
                  <span className="emailListText sponsor-card-email-text">
                    Email List
                  </span>
                  <span className="emailPopUp">
                    <PopUp
                      headerText={`${shorthandKeyName} Emails`}
                      message={emailListHtml}
                    />
                  </span>
                </span>
              </button>
            </div>
            <div className="text-left sponsor-card-name-cell">
              {namesFlat.length > 2 ? <div>{namesFlat[2]}</div> : <div></div>}
            </div>
          </>
        )}
        {!entry && (
          <>
            <div className="text-right"></div>
            <div className="text-left sponsor-card-name-cell"></div>
            <div className="text-right"></div>
            <div className="text-left sponsor-card-name-cell"></div>
          </>
        )}
        {renderExtraNameRows(entry, namesFlat, keyName)}
      </div>
    </div>
  );
};

export default SponsorCard;
